/**
 * AIGeneratorModal.jsx - AI Email Generator Modal Component
 * 
 * Part 5 Checklist Items Covered:
 * - M1: AI Modal Overlay (dark backdrop)
 * - M2: AI Modal Card (large content container)
 * - M3: Message Type Selector (Save the Date/Invitation/Reminder/RSVP)
 * - M4: Tone Selector (Elegant/Luxury/Formal/Modern)
 * - M5: Audience Selector (VIP/Family/All Invited)
 * - M6: Language Selector (English/Spanish/Bilingual)
 * - M7: Purpose Textarea ("What should this message say?")
 * - M8: Enhancement Chips (Persuasive/Personal Touch/Event Details/etc)
 * - M9: Event Details Inputs (Date/Venue/Hosted By overrides)
 * - M10: Generate Button (Trigger AI generation)
 * - M11: Generated Preview (Subject + body display)
 * - M12: Preview Mode Toggle (Email/Subject/Mobile views)
 * - M13: Refine Button (Re-generate with tweaks)
 * - M14: Save as Draft Button (Save template as draft)
 * - M15: Send Now Button (Save and send immediately)
 * - M16: Close Button (Dismiss modal)
 * 
 * Backend-Dependent: Requires /api/templates/generate endpoint
 * Currently uses mock generation for UI demonstration
 */

import React, { useState } from 'react';
import { 
  X, Sparkles, Wand2, Monitor, Smartphone, FileText, Send, 
  RefreshCw, AlertTriangle, Check, Calendar, MapPin, User
} from 'lucide-react';
import { COLORS } from './messagingStyles';

// ─── Constants ──────────────────────────────────────────────────────────────────

// M3: Message Type Options
const MESSAGE_TYPES = [
  { key: 'save_the_date', label: 'Save the Date' },
  { key: 'invitation', label: 'Invitation' },
  { key: 'reminder', label: 'Reminder' },
  { key: 'rsvp', label: 'RSVP Follow-up' },
  { key: 'thank_you', label: 'Thank You' },
];

// M4: Tone Options
const TONE_OPTIONS = [
  { key: 'elegant', label: 'Elegant' },
  { key: 'luxury', label: 'Luxury' },
  { key: 'formal', label: 'Formal' },
  { key: 'modern', label: 'Modern' },
  { key: 'warm', label: 'Warm & Personal' },
];

// M5: Audience Options
const AUDIENCE_OPTIONS = [
  { key: 'all', label: 'All Invited' },
  { key: 'vip', label: 'VIP Guests' },
  { key: 'family', label: 'Family' },
  { key: 'friends', label: 'Friends' },
];

// M6: Language Options
const LANGUAGE_OPTIONS = [
  { key: 'en', label: 'English' },
  { key: 'es', label: 'Spanish' },
  { key: 'bilingual', label: 'Bilingual' },
];

// M8: Enhancement Options
const ENHANCEMENT_OPTIONS = [
  { key: 'persuasive', label: 'Persuasive' },
  { key: 'personal', label: 'Personal Touch' },
  { key: 'event_details', label: 'Event Details' },
  { key: 'urgency', label: 'Urgency' },
  { key: 'excitement', label: 'Build Excitement' },
  { key: 'gratitude', label: 'Gratitude' },
];

// ─── Styles ─────────────────────────────────────────────────────────────────────

const styles = {
  // M1: AI Modal Overlay
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: 24,
  },
  
  // M2: AI Modal Card
  card: {
    width: '100%',
    maxWidth: 1000,
    maxHeight: '90vh',
    borderRadius: 20,
    background: COLORS.surface,
    boxShadow: '0 24px 48px rgba(0,0,0,0.3)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  
  // Header
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 24px',
    borderBottom: `1px solid ${COLORS.borderLight}`,
    background: 'linear-gradient(135deg, rgba(198,167,90,0.08) 0%, rgba(198,167,90,0.02) 100%)',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  headerIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    background: `linear-gradient(135deg, ${COLORS.gold} 0%, #D4B96A 100%)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 12px rgba(198,167,90,0.3)',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 700,
    color: COLORS.textPrimary,
  },
  headerSubtitle: {
    fontSize: 12,
    color: COLORS.textMuted,
    marginTop: 2,
  },
  
  // M16: Close Button
  closeBtn: {
    width: 40,
    height: 40,
    borderRadius: 10,
    border: `1px solid ${COLORS.borderMedium}`,
    background: COLORS.surface,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  
  // Backend Notice
  backendNotice: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '12px 24px',
    background: 'rgba(245,158,11,0.08)',
    borderBottom: '1px solid rgba(245,158,11,0.15)',
  },
  backendNoticeText: {
    fontSize: 12,
    color: '#92400e',
    fontWeight: 500,
  },
  
  // Body Layout
  body: {
    flex: 1,
    display: 'flex',
    overflow: 'hidden',
  },
  
  // Left Panel (Settings)
  leftPanel: {
    width: 380,
    borderRight: `1px solid ${COLORS.borderLight}`,
    padding: 24,
    overflowY: 'auto',
  },
  
  // Right Panel (Preview)
  rightPanel: {
    flex: 1,
    padding: 24,
    overflowY: 'auto',
    background: '#FAFAF8',
  },
  
  // Section
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: COLORS.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: 10,
  },
  
  // Chip Row
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
  chip: {
    padding: '8px 12px',
    borderRadius: 8,
    border: `1px solid ${COLORS.borderMedium}`,
    background: COLORS.surface,
    fontSize: 12,
    fontWeight: 600,
    color: COLORS.textSecondary,
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  chipActive: {
    borderColor: COLORS.gold,
    background: COLORS.goldLight,
    color: COLORS.goldDark,
  },
  chipSmall: {
    padding: '6px 10px',
    fontSize: 11,
  },
  
  // M7: Purpose Textarea
  textarea: {
    width: '100%',
    minHeight: 100,
    padding: '12px 14px',
    borderRadius: 10,
    border: `1px solid ${COLORS.borderMedium}`,
    background: COLORS.inputBg,
    fontSize: 13,
    color: COLORS.textPrimary,
    resize: 'vertical',
    outline: 'none',
    fontFamily: 'inherit',
  },
  
  // M9: Event Details Inputs
  eventDetailsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
  },
  eventInput: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 8,
    border: `1px solid ${COLORS.borderMedium}`,
    background: COLORS.inputBg,
    fontSize: 13,
    color: COLORS.textPrimary,
    outline: 'none',
  },
  inputWithIcon: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  inputIcon: {
    width: 32,
    height: 32,
    borderRadius: 8,
    background: COLORS.chipBg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  
  // M10: Generate Button
  generateBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    width: '100%',
    padding: '14px 20px',
    borderRadius: 12,
    border: 'none',
    background: `linear-gradient(135deg, ${COLORS.gold} 0%, #D4B96A 100%)`,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 700,
    color: '#FFFFFF',
    transition: 'all 0.2s',
    boxShadow: '0 4px 12px rgba(198,167,90,0.25)',
  },
  generateBtnDisabled: {
    background: '#E4DBC9',
    cursor: 'not-allowed',
    boxShadow: 'none',
  },
  
  // Preview Area
  previewHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  previewTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: COLORS.textPrimary,
  },
  
  // M12: Preview Mode Toggle
  previewModeToggle: {
    display: 'flex',
    gap: 4,
    padding: 4,
    borderRadius: 8,
    background: COLORS.chipBg,
  },
  previewModeBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 36,
    height: 32,
    borderRadius: 6,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  previewModeBtnActive: {
    background: COLORS.gold,
  },
  
  // M11: Generated Preview
  previewCard: {
    borderRadius: 14,
    border: `1px solid ${COLORS.borderLight}`,
    background: COLORS.surface,
    overflow: 'hidden',
    minHeight: 300,
  },
  previewSubject: {
    padding: '14px 18px',
    borderBottom: `1px solid ${COLORS.borderLight}`,
    background: COLORS.chipBg,
  },
  previewSubjectLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: COLORS.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: 4,
  },
  previewSubjectText: {
    fontSize: 14,
    fontWeight: 600,
    color: COLORS.textPrimary,
  },
  previewBody: {
    padding: 20,
  },
  previewBodyText: {
    fontSize: 14,
    lineHeight: 1.7,
    color: COLORS.textPrimary,
    whiteSpace: 'pre-wrap',
  },
  
  // Empty Preview
  emptyPreview: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 48,
    textAlign: 'center',
    minHeight: 300,
  },
  emptyIcon: {
    width: 64,
    height: 64,
    borderRadius: 16,
    background: COLORS.goldLight,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  emptyTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: COLORS.textPrimary,
    marginBottom: 8,
  },
  emptyBody: {
    fontSize: 13,
    color: COLORS.textMuted,
    maxWidth: 280,
    lineHeight: 1.5,
  },
  
  // Mobile Preview Frame
  mobileFrame: {
    width: 320,
    margin: '0 auto',
    borderRadius: 24,
    border: '8px solid #1A1A1A',
    background: COLORS.surface,
    overflow: 'hidden',
    boxShadow: '0 12px 32px rgba(0,0,0,0.15)',
  },
  
  // Footer Actions
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 12,
    padding: '16px 24px',
    borderTop: `1px solid ${COLORS.borderLight}`,
  },
  footerBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 20px',
    borderRadius: 10,
    border: `1px solid ${COLORS.borderMedium}`,
    background: COLORS.surface,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    color: COLORS.textSecondary,
    transition: 'all 0.15s',
  },
  footerBtnPrimary: {
    border: 'none',
    background: `linear-gradient(135deg, ${COLORS.gold} 0%, #D4B96A 100%)`,
    color: '#FFFFFF',
    fontWeight: 700,
    boxShadow: '0 4px 12px rgba(198,167,90,0.25)',
  },
  footerBtnDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
};

// ─── Mock Generator ─────────────────────────────────────────────────────────────

const mockGenerateContent = (config) => {
  // Simulated AI-generated content based on message type
  const templates = {
    save_the_date: {
      subject: 'Save the Date - A Special Celebration Awaits',
      body: `Dear Guest,

We are thrilled to share some wonderful news with you!

Please mark your calendar and save the date for our upcoming celebration. We would be honored to have you join us for this special occasion.

${config.eventDate ? `Date: ${config.eventDate}` : 'Date: To be announced'}
${config.venue ? `Venue: ${config.venue}` : ''}
${config.hostedBy ? `Hosted by: ${config.hostedBy}` : ''}

A formal invitation with all the details will follow soon. We cannot wait to celebrate with you!

With warmest regards`,
    },
    invitation: {
      subject: 'You\'re Cordially Invited to Our Special Celebration',
      body: `Dear Honored Guest,

It is with great joy that we extend this invitation to you.

${config.eventDate ? `Date: ${config.eventDate}` : 'Date: To be announced'}
${config.venue ? `Venue: ${config.venue}` : ''}
${config.hostedBy ? `Hosted by: ${config.hostedBy}` : ''}

Your presence would mean the world to us on this momentous day. We have planned an evening filled with joy, laughter, and beautiful memories.

Please RSVP at your earliest convenience.

With love and anticipation`,
    },
    reminder: {
      subject: 'Reminder: Our Special Day is Approaching',
      body: `Dear Guest,

We hope this message finds you well!

This is a friendly reminder that our celebration is just around the corner. We are counting down the days and cannot wait to see you there.

${config.eventDate ? `Date: ${config.eventDate}` : ''}
${config.venue ? `Venue: ${config.venue}` : ''}

If you haven't already, please confirm your attendance. We want to ensure everything is perfect for your arrival.

See you soon!`,
    },
    rsvp: {
      subject: 'We\'d Love to Hear from You - RSVP Request',
      body: `Dear Guest,

We noticed we haven't received your RSVP yet, and we'd love to know if you'll be joining us!

Your presence at our celebration would make it even more special. Please take a moment to let us know if you can attend.

${config.eventDate ? `Event Date: ${config.eventDate}` : ''}
${config.venue ? `Location: ${config.venue}` : ''}

Simply reply to this email or use the RSVP link below. We hope to see you there!

Warmly`,
    },
    thank_you: {
      subject: 'Thank You for Being Part of Our Special Day',
      body: `Dear Guest,

We are still floating on cloud nine from our wonderful celebration, and it's all thanks to people like you!

Your presence, love, and support made our day absolutely perfect. We are so grateful to have you in our lives.

Thank you for celebrating with us and for the beautiful memories we created together.

With heartfelt gratitude`,
    },
  };

  const template = templates[config.messageType] || templates.invitation;
  return {
    subject: template.subject,
    body: template.body,
  };
};

// ─── Component ──────────────────────────────────────────────────────────────────

const AIGeneratorModal = ({ onClose, onSaveAsDraft, onSendNow }) => {
  // Config state
  const [messageType, setMessageType] = useState('invitation');
  const [tone, setTone] = useState('elegant');
  const [audience, setAudience] = useState('all');
  const [language, setLanguage] = useState('en');
  const [purpose, setPurpose] = useState('');
  const [enhancements, setEnhancements] = useState([]);
  const [eventDate, setEventDate] = useState('');
  const [venue, setVenue] = useState('');
  const [hostedBy, setHostedBy] = useState('');
  
  // Generated content state
  const [generatedSubject, setGeneratedSubject] = useState('');
  const [generatedBody, setGeneratedBody] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  
  // Preview state
  const [previewMode, setPreviewMode] = useState('email'); // 'email' | 'subject' | 'mobile'
  
  // Toggle enhancement
  const toggleEnhancement = (key) => {
    setEnhancements(prev => 
      prev.includes(key) 
        ? prev.filter(e => e !== key)
        : [...prev, key]
    );
  };

  // M10: Generate handler
  const handleGenerate = async () => {
    setIsGenerating(true);
    
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Mock generation (backend-dependent: would call /api/templates/generate)
    const config = {
      messageType,
      tone,
      audience,
      language,
      purpose,
      enhancements,
      eventDate,
      venue,
      hostedBy,
    };
    
    const generated = mockGenerateContent(config);
    setGeneratedSubject(generated.subject);
    setGeneratedBody(generated.body);
    setHasGenerated(true);
    setIsGenerating(false);
  };

  // M13: Refine handler
  const handleRefine = () => {
    handleGenerate();
  };

  // M14: Save as Draft handler
  const handleSaveAsDraft = () => {
    if (onSaveAsDraft) {
      onSaveAsDraft({
        name: `AI Generated - ${messageType}`,
        subject: generatedSubject,
        body: generatedBody,
        type: messageType,
        status: 'draft',
      });
    }
    onClose();
  };

  // M15: Send Now handler
  const handleSendNow = () => {
    if (onSendNow) {
      onSendNow({
        subject: generatedSubject,
        body: generatedBody,
      });
    }
    onClose();
  };

  return (
    // M1: AI Modal Overlay
    <div style={styles.overlay} onClick={onClose} data-testid="ai-generator-modal">
      {/* M2: AI Modal Card */}
      <div style={styles.card} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <div style={styles.headerIcon}>
              <Sparkles size={22} color="#FFFFFF" />
            </div>
            <div>
              <div style={styles.headerTitle}>AI Email Generator</div>
              <div style={styles.headerSubtitle}>Create personalized messages with AI assistance</div>
            </div>
          </div>
          
          {/* M16: Close Button */}
          <button
            style={styles.closeBtn}
            onClick={onClose}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = COLORS.goldLight;
              e.currentTarget.style.borderColor = COLORS.gold;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = COLORS.surface;
              e.currentTarget.style.borderColor = COLORS.borderMedium;
            }}
            data-testid="ai-close-btn"
          >
            <X size={18} color={COLORS.textSecondary} />
          </button>
        </div>
        
        {/* Backend Notice */}
        <div style={styles.backendNotice} data-testid="ai-backend-notice">
          <AlertTriangle size={16} color="#f59e0b" />
          <span style={styles.backendNoticeText}>
            <strong>Backend-Dependent:</strong> Currently using mock generation. Requires /api/templates/generate endpoint for real AI.
          </span>
        </div>
        
        {/* Body */}
        <div style={styles.body}>
          {/* Left Panel - Settings */}
          <div style={styles.leftPanel}>
            {/* M3: Message Type Selector */}
            <div style={styles.section}>
              <div style={styles.sectionTitle}>Message Type</div>
              <div style={styles.chipRow}>
                {MESSAGE_TYPES.map(type => (
                  <button
                    key={type.key}
                    style={{
                      ...styles.chip,
                      ...(messageType === type.key ? styles.chipActive : {}),
                    }}
                    onClick={() => setMessageType(type.key)}
                    type="button"
                    data-testid={`ai-type-${type.key}`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* M4: Tone Selector */}
            <div style={styles.section}>
              <div style={styles.sectionTitle}>Tone</div>
              <div style={styles.chipRow}>
                {TONE_OPTIONS.map(opt => (
                  <button
                    key={opt.key}
                    style={{
                      ...styles.chip,
                      ...styles.chipSmall,
                      ...(tone === opt.key ? styles.chipActive : {}),
                    }}
                    onClick={() => setTone(opt.key)}
                    type="button"
                    data-testid={`ai-tone-${opt.key}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* M5: Audience Selector */}
            <div style={styles.section}>
              <div style={styles.sectionTitle}>Audience</div>
              <div style={styles.chipRow}>
                {AUDIENCE_OPTIONS.map(opt => (
                  <button
                    key={opt.key}
                    style={{
                      ...styles.chip,
                      ...styles.chipSmall,
                      ...(audience === opt.key ? styles.chipActive : {}),
                    }}
                    onClick={() => setAudience(opt.key)}
                    type="button"
                    data-testid={`ai-audience-${opt.key}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* M6: Language Selector */}
            <div style={styles.section}>
              <div style={styles.sectionTitle}>Language</div>
              <div style={styles.chipRow}>
                {LANGUAGE_OPTIONS.map(opt => (
                  <button
                    key={opt.key}
                    style={{
                      ...styles.chip,
                      ...styles.chipSmall,
                      ...(language === opt.key ? styles.chipActive : {}),
                    }}
                    onClick={() => setLanguage(opt.key)}
                    type="button"
                    data-testid={`ai-lang-${opt.key}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* M7: Purpose Textarea */}
            <div style={styles.section}>
              <div style={styles.sectionTitle}>Purpose / Instructions</div>
              <textarea
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                placeholder="What should this message convey? Any specific details to include?"
                style={styles.textarea}
                data-testid="ai-purpose-input"
              />
            </div>
            
            {/* M8: Enhancement Chips */}
            <div style={styles.section}>
              <div style={styles.sectionTitle}>Enhancements</div>
              <div style={styles.chipRow}>
                {ENHANCEMENT_OPTIONS.map(opt => (
                  <button
                    key={opt.key}
                    style={{
                      ...styles.chip,
                      ...styles.chipSmall,
                      ...(enhancements.includes(opt.key) ? styles.chipActive : {}),
                    }}
                    onClick={() => toggleEnhancement(opt.key)}
                    type="button"
                    data-testid={`ai-enhance-${opt.key}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* M9: Event Details Inputs */}
            <div style={styles.section}>
              <div style={styles.sectionTitle}>Event Details (Optional)</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={styles.inputWithIcon}>
                  <div style={styles.inputIcon}>
                    <Calendar size={14} color={COLORS.textMuted} />
                  </div>
                  <input
                    type="text"
                    value={eventDate}
                    onChange={(e) => setEventDate(e.target.value)}
                    placeholder="Event Date"
                    style={{ ...styles.eventInput, flex: 1 }}
                    data-testid="ai-event-date"
                  />
                </div>
                <div style={styles.inputWithIcon}>
                  <div style={styles.inputIcon}>
                    <MapPin size={14} color={COLORS.textMuted} />
                  </div>
                  <input
                    type="text"
                    value={venue}
                    onChange={(e) => setVenue(e.target.value)}
                    placeholder="Venue"
                    style={{ ...styles.eventInput, flex: 1 }}
                    data-testid="ai-venue"
                  />
                </div>
                <div style={styles.inputWithIcon}>
                  <div style={styles.inputIcon}>
                    <User size={14} color={COLORS.textMuted} />
                  </div>
                  <input
                    type="text"
                    value={hostedBy}
                    onChange={(e) => setHostedBy(e.target.value)}
                    placeholder="Hosted By"
                    style={{ ...styles.eventInput, flex: 1 }}
                    data-testid="ai-hosted-by"
                  />
                </div>
              </div>
            </div>
            
            {/* M10: Generate Button */}
            <button
              style={{
                ...styles.generateBtn,
                ...(isGenerating ? styles.generateBtnDisabled : {}),
              }}
              onClick={handleGenerate}
              disabled={isGenerating}
              data-testid="ai-generate-btn"
            >
              {isGenerating ? (
                <>
                  <RefreshCw size={18} style={{ animation: 'spin 1s linear infinite' }} />
                  Generating...
                </>
              ) : (
                <>
                  <Wand2 size={18} />
                  Generate Message
                </>
              )}
            </button>
          </div>
          
          {/* Right Panel - Preview */}
          <div style={styles.rightPanel}>
            {/* M11: Generated Preview */}
            <div style={styles.previewHeader}>
              <div style={styles.previewTitle}>Preview</div>
              
              {/* M12: Preview Mode Toggle */}
              <div style={styles.previewModeToggle}>
                <button
                  style={{
                    ...styles.previewModeBtn,
                    ...(previewMode === 'email' ? styles.previewModeBtnActive : {}),
                  }}
                  onClick={() => setPreviewMode('email')}
                  data-testid="ai-preview-email"
                >
                  <Monitor size={16} color={previewMode === 'email' ? '#FFFFFF' : COLORS.textMuted} />
                </button>
                <button
                  style={{
                    ...styles.previewModeBtn,
                    ...(previewMode === 'subject' ? styles.previewModeBtnActive : {}),
                  }}
                  onClick={() => setPreviewMode('subject')}
                  data-testid="ai-preview-subject"
                >
                  <FileText size={16} color={previewMode === 'subject' ? '#FFFFFF' : COLORS.textMuted} />
                </button>
                <button
                  style={{
                    ...styles.previewModeBtn,
                    ...(previewMode === 'mobile' ? styles.previewModeBtnActive : {}),
                  }}
                  onClick={() => setPreviewMode('mobile')}
                  data-testid="ai-preview-mobile"
                >
                  <Smartphone size={16} color={previewMode === 'mobile' ? '#FFFFFF' : COLORS.textMuted} />
                </button>
              </div>
            </div>
            
            {hasGenerated ? (
              previewMode === 'mobile' ? (
                <div style={styles.mobileFrame}>
                  <div style={styles.previewCard}>
                    <div style={styles.previewSubject}>
                      <div style={styles.previewSubjectLabel}>Subject</div>
                      <div style={styles.previewSubjectText}>{generatedSubject}</div>
                    </div>
                    <div style={styles.previewBody}>
                      <div style={styles.previewBodyText}>{generatedBody}</div>
                    </div>
                  </div>
                </div>
              ) : previewMode === 'subject' ? (
                <div style={styles.previewCard}>
                  <div style={{ padding: 24 }}>
                    <div style={styles.sectionTitle}>Subject Line Only</div>
                    <div style={{ 
                      fontSize: 18, 
                      fontWeight: 600, 
                      color: COLORS.textPrimary,
                      marginTop: 12,
                      padding: 16,
                      background: COLORS.chipBg,
                      borderRadius: 10,
                    }}>
                      {generatedSubject}
                    </div>
                  </div>
                </div>
              ) : (
                <div style={styles.previewCard}>
                  <div style={styles.previewSubject}>
                    <div style={styles.previewSubjectLabel}>Subject</div>
                    <div style={styles.previewSubjectText}>{generatedSubject}</div>
                  </div>
                  <div style={styles.previewBody}>
                    <div style={styles.previewBodyText}>{generatedBody}</div>
                  </div>
                </div>
              )
            ) : (
              <div style={styles.previewCard}>
                <div style={styles.emptyPreview}>
                  <div style={styles.emptyIcon}>
                    <Sparkles size={28} color={COLORS.gold} />
                  </div>
                  <div style={styles.emptyTitle}>Ready to Generate</div>
                  <div style={styles.emptyBody}>
                    Configure your message settings and click "Generate Message" to create AI-powered content.
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Footer */}
        <div style={styles.footer}>
          {/* M13: Refine Button */}
          {hasGenerated && (
            <button
              style={styles.footerBtn}
              onClick={handleRefine}
              disabled={isGenerating}
              data-testid="ai-refine-btn"
            >
              <RefreshCw size={16} />
              Refine
            </button>
          )}
          
          {/* M14: Save as Draft Button */}
          <button
            style={{
              ...styles.footerBtn,
              ...(!hasGenerated ? styles.footerBtnDisabled : {}),
            }}
            onClick={handleSaveAsDraft}
            disabled={!hasGenerated}
            data-testid="ai-save-draft-btn"
          >
            <FileText size={16} />
            Save as Draft
          </button>
          
          {/* M15: Send Now Button */}
          <button
            style={{
              ...styles.footerBtn,
              ...styles.footerBtnPrimary,
              ...(!hasGenerated ? styles.footerBtnDisabled : {}),
            }}
            onClick={handleSendNow}
            disabled={!hasGenerated}
            data-testid="ai-send-now-btn"
          >
            <Send size={16} />
            Send Now
          </button>
        </div>
      </div>
      
      {/* CSS Animation for spinner */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AIGeneratorModal;
